import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { GOOGLE_MAP_API_KEY } from "../../Constatns";
import { IconButton } from "@mui/material";
import PrimaryButton from "../subComponents/buttons/PrimaryButton";
import practicelocationicon from "../../assets/images/practicelocationicon.svg";
import closeModal from "../../assets/images/closeModal.svg";
import { t } from "../../locales/translate"; 

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "400px" };
const defaultCenter = { lat: 31.5204, lng: 74.3587 };

const LocationPickerModal = ({
  isOpen,
  onClose,
  onChooseLocation,
  initialLocation,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  });

  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
  const [map, setMap] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialLocation?.lat && initialLocation?.lng) {
      setMarkerPosition({ lat: initialLocation.lat, lng: initialLocation.lng });
    }
  }, [isOpen, initialLocation]);

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const geocodePosition = (lat, lng) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setSelectedAddress(results[0].formatted_address);
      }
    });
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarkerPosition({ lat, lng });
    geocodePosition(lat, lng);
  };

  const handleMarkerDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarkerPosition({ lat, lng });
    geocodePosition(lat, lng);
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMarkerPosition({ lat, lng });
        setSelectedAddress(place.formatted_address);
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(17);
        }
      }
    }
  };

  const handleConfirm = () => {
    onChooseLocation({
      lat: markerPosition.lat,
      lng: markerPosition.lng,
      address: selectedAddress,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="dynamicmodal-overlay" style={{ zIndex: 999999 }}>
      <div
        className="dynamicmodal-container overlay-wrapper"
        style={{ width: "720px" }}
      >
        <div className="dynamicmodal-header mb-3">
          <div className="dynamicmodal-header-left">
            <div className="icon">
              <img src={practicelocationicon} alt="practicelocationicon" />
            </div>
            <div className="text">
              <h2 className="modalheading">{t("addPracticeLocation")}</h2>
              <p className="modaldescription">
                {t("addPracticeLocationTip")}
              </p>
            </div>
          </div>
          <div className="dynamicmodal-header-right">
            <IconButton onClick={onClose}>
              <img src={closeModal} alt="Close" />
            </IconButton>
          </div>
        </div>

        <div
          className="dynamicmodal-body"
          style={{ position: "relative", padding: 0 }}
        >
          {isLoaded ? (
            <>
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  left: "10px",
                  zIndex: 10,
                  width: "60%",
                }}
              >
                <Autocomplete
                  onLoad={(autocomplete) =>
                    (autocompleteRef.current = autocomplete)
                  }
                  onPlaceChanged={handlePlaceChanged}
                >
                  <input
                    type="text"
                    placeholder={t('search')}
                    style={{
                      width: "100%",
                      height: "40px",
                      padding: "0 15px",
                      borderRadius: "20px",
                      border: "none",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                      outline: "none",
                    }}
                  />
                </Autocomplete>
              </div>

              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={markerPosition}
                zoom={14}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onClick={handleMapClick}
                options={{ mapTypeControl: false, streetViewControl: false }}
              >
                <Marker
                  position={markerPosition}
                  draggable={true}
                  onDragEnd={handleMarkerDragEnd}
                  animation={window.google.maps.Animation.DROP}
                />
              </GoogleMap>
            </>
          ) : (
            <div
              style={{
                height: "400px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {t("loadingMap")}
            </div>
          )}
        </div>

        <div
          className="dynamicmodal-footer"
          style={{ justifyContent: "flex-end", marginTop: "15px" }}
        >
          <PrimaryButton
            text={t("chooseLocation")}
            onClick={handleConfirm}
            width={"120px"}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
